import { useCallback, useEffect, useMemo, useState } from 'react'
import { API_ENDPOINTS } from '../../config/api'
import { Button } from '../ui/Button'
import { EnterpriseOperationalCard } from '../enterprise-operational/EnterpriseOperationalSection'
import entStyles from '../../styles/enterprise/EnterpriseOperationalUx.module.scss'
import PlatformAiProviderAccessModal, {
  type AccessGrant,
} from './PlatformAiProviderAccessModal'

type PlatformProvider = {
  id: string
  providerKey: string
  displayName: string
  enabled: boolean
  apiKeyMasked?: string | null
  hasApiKey?: boolean
  baseUrl?: string | null
  model?: string | null
  grantCount?: number
  ownership?: string
}

type GrantRow = AccessGrant & {
  providerKey?: string | null
  displayName?: string | null
}

type Props = {
  token: string | null
  isPlatformAdmin: boolean
  t: (key: string, fallback: string) => string
}

/**
 * Platform AI Providers — PLATFORM_MANAGED only.
 * Ordinary users never see this panel; never exposes raw keys.
 * Org access is managed by name via modal (AI-6.1) — UUIDs stay in API only.
 */
export default function PlatformAiProvidersPanel({ token, isPlatformAdmin, t }: Props) {
  const [providers, setProviders] = useState<PlatformProvider[]>([])
  const [grants, setGrants] = useState<GrantRow[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const [providerKey, setProviderKey] = useState('xai_grok')
  const [displayName, setDisplayName] = useState('xAI Grok')
  const [apiKey, setApiKey] = useState('')
  const [baseUrl, setBaseUrl] = useState('https://api.x.ai/v1')
  const [model, setModel] = useState('grok-2-latest')
  const [enabled, setEnabled] = useState(true)

  const [accessModalProvider, setAccessModalProvider] = useState<PlatformProvider | null>(null)

  const load = useCallback(async () => {
    if (!token || !isPlatformAdmin) return
    const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
    setLoading(true)
    setError(null)
    try {
      const [pRes, gRes] = await Promise.all([
        fetch(API_ENDPOINTS.AI_PLATFORM_PROVIDERS, { headers }),
        fetch(API_ENDPOINTS.AI_PLATFORM_PROVIDER_GRANTS, { headers }),
      ])
      if (pRes.status === 403) {
        setError(t('integrations.ai.platformAdminRequired', 'Platform administrator access required'))
        return
      }
      const pJson = await pRes.json().catch(() => ({}))
      const gJson = await gRes.json().catch(() => ({}))
      if (!pRes.ok) throw new Error(pJson?.message || 'Failed to load platform providers')
      setProviders(Array.isArray(pJson?.data?.providers) ? pJson.data.providers : [])
      setGrants(Array.isArray(gJson?.data?.grants) ? gJson.data.grants : [])
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Load failed')
    } finally {
      setLoading(false)
    }
  }, [token, isPlatformAdmin, t])

  useEffect(() => {
    void load()
  }, [load])

  const grantsForModalProvider = useMemo(() => {
    if (!accessModalProvider) return []
    return grants.filter(
      (g) =>
        g.platformProviderId === accessModalProvider.id ||
        g.providerKey === accessModalProvider.providerKey,
    )
  }, [grants, accessModalProvider])

  if (!isPlatformAdmin) return null

  const saveProvider = async () => {
    if (!token) return
    const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
    setError(null)
    try {
      const body: Record<string, unknown> = {
        providerKey,
        displayName,
        baseUrl,
        model,
        enabled,
        ownership: 'PLATFORM_MANAGED',
      }
      if (apiKey.trim()) body.apiKey = apiKey.trim()
      const res = await fetch(API_ENDPOINTS.AI_PLATFORM_PROVIDERS, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(json?.message || 'Save failed')
      setApiKey('')
      void load()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed')
    }
  }

  const enabledGrants = grants.filter((g) => g.enabled)

  return (
    <section className={entStyles.layer} data-testid="platform-ai-providers">
      <p className={entStyles.layerLabel}>
        {t('integrations.ai.platformProviders', 'Platform AI Providers')}
      </p>
      <p className={entStyles.metaRow}>
        {t(
          'integrations.ai.platformProvidersNote',
          'PLATFORM_MANAGED credentials. Keys are never shown to tenant users. Personal keys are not supported.',
        )}
      </p>
      {loading ? <p className={entStyles.metaRow}>{t('common.loading', 'Loading…')}</p> : null}
      {error ? (
        <p className="text-red-600 text-sm" role="alert">
          {error}
        </p>
      ) : null}

      <EnterpriseOperationalCard title={t('integrations.ai.upsertPlatformProvider', 'Configure platform provider')}>
        <form
          className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm"
          onSubmit={(e) => {
            e.preventDefault()
            void saveProvider()
          }}
        >
          <label className="flex flex-col gap-1">
            <span>providerKey</span>
            <select className="input" value={providerKey} onChange={(e) => setProviderKey(e.target.value)}>
              <option value="xai_grok">xai_grok</option>
              <option value="openai">openai</option>
              <option value="google_gemini">google_gemini</option>
              <option value="openai_compatible">openai_compatible</option>
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span>{t('integrations.ai.displayName', 'Display name')}</span>
            <input className="input" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
          </label>
          <label className="flex flex-col gap-1">
            <span>{t('integrations.ai.apiKey', 'API key')}</span>
            <input
              className="input"
              type="password"
              autoComplete="off"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span>{t('integrations.ai.baseUrl', 'Base URL')}</span>
            <input className="input" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} />
          </label>
          <label className="flex flex-col gap-1">
            <span>{t('integrations.ai.model', 'Model')}</span>
            <input className="input" value={model} onChange={(e) => setModel(e.target.value)} />
          </label>
          <label className="flex items-center gap-2 mt-6">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            <span>{t('integrations.ai.enabled', 'Enabled')}</span>
          </label>
          <div className="md:col-span-2">
            <Button type="submit" size="small" color="brand">
              {t('common.save', 'Save')}
            </Button>
          </div>
        </form>
      </EnterpriseOperationalCard>

      <div className={entStyles.standardGrid} style={{ marginTop: 12 }}>
        {providers.map((p) => {
          const activeCount = grants.filter(
            (g) => g.enabled && (g.platformProviderId === p.id || g.providerKey === p.providerKey),
          ).length
          return (
            <EnterpriseOperationalCard key={p.id} title={p.displayName}>
              <p className={entStyles.metaRow}>
                <span>{p.providerKey}</span>
                <span>{p.enabled ? t('integrations.ai.enabled', 'Enabled') : t('integrations.ai.disabled', 'Disabled')}</span>
                <span>
                  {t('integrations.ai.maskedKey', 'Key')}: {p.apiKeyMasked || '****'}
                </span>
                <span>
                  {t('integrations.ai.grants', 'Grants')}: {activeCount || p.grantCount || 0}
                </span>
              </p>
              <Button
                type="button"
                size="small"
                color="brand"
                onClick={() => setAccessModalProvider(p)}
              >
                {t('integrations.ai.manageAccess', 'Manage access')}
              </Button>
            </EnterpriseOperationalCard>
          )
        })}
      </div>

      {providers.length === 0 && !loading ? (
        <p className={entStyles.metaRow}>
          {t('integrations.ai.noPlatformProviders', 'No platform providers configured yet.')}
        </p>
      ) : null}

      <div className={entStyles.standardGrid} style={{ marginTop: 12 }}>
        {enabledGrants.map((g) => (
          <EnterpriseOperationalCard
            key={g.id}
            title={g.organizationName || t('integrations.ai.unknownOrganization', 'Organization')}
          >
            <p className={entStyles.metaRow}>
              <span>{g.displayName || g.providerKey}</span>
              <span>{t('integrations.ai.accessGranted', 'Access granted')}</span>
            </p>
          </EnterpriseOperationalCard>
        ))}
      </div>

      {token && accessModalProvider ? (
        <PlatformAiProviderAccessModal
          open={Boolean(accessModalProvider)}
          token={token}
          providerId={accessModalProvider.id}
          providerLabel={accessModalProvider.displayName || accessModalProvider.providerKey}
          initialGrants={grantsForModalProvider}
          t={t}
          onClose={() => setAccessModalProvider(null)}
          onApplied={() => {
            void load()
          }}
        />
      ) : null}
    </section>
  )
}
